import React, { useMemo, useEffect, useState } from 'react';
import { PositionLayoutEnum } from '../../types/campaignsApiResponse.d';
import { useThrottledWindowSize } from '../useThrottledWindowSize';
import {
	getCenterLeftResponsive,
	getCenterTopResponsive,
	getOffsetBottomResponsive,
	getOffsetLeftResponsive,
	getOffsetRightResponsive,
	getOffsetTopResponsive,
} from './getResponsiveStyles';

type ApplyPositionT = {
	top?: string;
	left?: string;
	right?: string;
	bottom?: string;
};

type GetPositionCssT = {
	layout: PositionLayoutEnum;
	offsetX: number;
	offsetY: number;
	widthWithPadding: number;
	heightWithPadding: number;
	windowWidth: number;
	windowHeight: number;
};

function getHalfDimension(
	height: number = 0,
	width: number = 0
): {
	halfHeight: number;
	halfWidth: number;
} {
	const halfHeight = height / 2;
	const halfWidth = width / 2;
	return { halfHeight, halfWidth };
}

function applyPosition({
	top = '',
	left = '',
	right = '',
	bottom = '',
}: ApplyPositionT) {
	let output: React.CSSProperties = {};
	if (top) {
		output = { ...output, paddingTop: top, top: 0, bottom: 0 };
	}
	if (left) {
		output = {
			...output,
			paddingLeft: left,
			left: 0,
			textAlign: 'left' as const,
			right: 0,
		};
	}
	if (right) {
		output = {
			...output,
			paddingRight: right,
			right: 0,
			textAlign: 'right' as const,
			left: 0,
		};
	}
	if (bottom) {
		output = {
			...output,
			paddingBottom: bottom,
			bottom: 0,
			top: 'auto',
		};
	}

	return output;
}

function getResponsivePositionStyle({
	layout,
	offsetX,
	offsetY,
	widthWithPadding,
	heightWithPadding,
	windowWidth,
	windowHeight,
}: GetPositionCssT): React.CSSProperties {
	const { halfWidth, halfHeight } = getHalfDimension(
		heightWithPadding,
		widthWithPadding
	);

	switch (layout) {
		case PositionLayoutEnum.CENTER: {
			return {
				...applyPosition({
					top: `calc(50vh - ${halfHeight}px)`,
					left: `calc(50% - ${halfWidth}px)`,
				}),
				...getCenterTopResponsive({
					offsetY,
					heightWithPadding,
					windowHeight,
				}),
				...getCenterLeftResponsive({
					widthWithPadding,
					windowWidth,
				}),
			};
		}
		case PositionLayoutEnum.TOP_CENTER: {
			return {
				...applyPosition({
					top: `${offsetY}px`,
					left: `calc(50% - ${halfWidth}px)`,
				}),
				...getCenterLeftResponsive({
					widthWithPadding,
					windowWidth,
				}),
				...getOffsetTopResponsive({
					offsetY,
					heightWithPadding,
					windowHeight,
				}),
			};
		}
		case PositionLayoutEnum.BOTTOM_CENTER: {
			return {
				...applyPosition({
					bottom: `${offsetY}px`,
					left: `calc(50% - ${halfWidth}px)`,
				}),
				...getCenterLeftResponsive({
					widthWithPadding,
					windowWidth,
				}),
				...getOffsetBottomResponsive({
					offsetY,
					heightWithPadding,
					windowHeight,
				}),
			};
		}
		case PositionLayoutEnum.CENTER_LEFT: {
			return {
				...applyPosition({
					top: `calc(50vh - ${halfHeight}px)`,
					left: `${offsetX}px`,
				}),
				...getCenterTopResponsive({
					offsetY,
					heightWithPadding,
					windowHeight,
				}),
				...getOffsetLeftResponsive({
					offsetX,
					widthWithPadding,
					windowWidth,
				}),
			};
		}
		case PositionLayoutEnum.CENTER_RIGHT: {
			return {
				...applyPosition({
					top: `calc(50vh - ${halfHeight}px)`,
					right: `${offsetX}px`,
				}),
				...getCenterTopResponsive({
					offsetY,
					heightWithPadding,
					windowHeight,
				}),
				...getOffsetRightResponsive({
					offsetX,
					widthWithPadding,
					windowWidth,
				}),
			};
		}
		case PositionLayoutEnum.TOP_LEFT: {
			return {
				...applyPosition({
					top: `${offsetY}px`,
					left: `${offsetX}px`,
				}),
				...getOffsetTopResponsive({
					offsetY,
					heightWithPadding,
					windowHeight,
				}),
				...getOffsetLeftResponsive({
					offsetX,
					widthWithPadding,
					windowWidth,
				}),
			};
		}
		case PositionLayoutEnum.TOP_RIGHT: {
			return {
				...applyPosition({
					top: `${offsetY}px`,
					right: `${offsetX}px`,
				}),
				...getOffsetTopResponsive({
					offsetY,
					heightWithPadding,
					windowHeight,
				}),
				...getOffsetRightResponsive({
					offsetX,
					widthWithPadding,
					windowWidth,
				}),
			};
		}
		case PositionLayoutEnum.BOTTOM_LEFT: {
			return {
				...applyPosition({
					bottom: `${offsetY}px`,
					left: `${offsetX}px`,
				}),
				...getOffsetLeftResponsive({
					offsetX,
					widthWithPadding,
					windowWidth,
				}),
				...getOffsetBottomResponsive({
					offsetY,
					heightWithPadding,
					windowHeight,
				}),
			};
		}
		default: {
			// BOTTOM_RIGHT
			return {
				...applyPosition({
					bottom: `${offsetY}px`,
					right: `${offsetX}px`,
				}),
				...getOffsetRightResponsive({
					offsetX,
					widthWithPadding,
					windowWidth,
				}),
				...getOffsetBottomResponsive({
					offsetY,
					heightWithPadding,
					windowHeight,
				}),
			};
		}
	}
}

export function useResponsivePosition({
	layout,
	offsetX,
	offsetY,
	onElSize,
}: {
	layout: PositionLayoutEnum;
	offsetX: number;
	offsetY: number;
	onElSize?: () => void;
}) {
	const [elementSize, recalcPosition] = useState({
		width: 0,
		height: 0,
	});
	const { width: windowWidth, height: windowHeight } = useThrottledWindowSize();

	useEffect(() => {
		if (
			elementSize.width !== 0 &&
			elementSize.height !== 0 &&
			typeof onElSize === 'function'
		) {
			onElSize();
		}
	}, [elementSize]);

	const responsivePositionStyle: React.CSSProperties = useMemo(
		() =>
			getResponsivePositionStyle({
				layout,
				offsetX,
				offsetY,
				widthWithPadding: elementSize.width,
				heightWithPadding: elementSize.height,
				windowWidth,
				windowHeight,
			}),
		[
			layout,
			offsetX,
			offsetY,
			elementSize.width,
			elementSize.height,
			windowWidth,
			windowHeight,
		]
	);

	return {
		responsivePositionStyle,
		recalcPosition,
	};
}
